import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getOrders, updateOrderStatus } from '../../firebase/database';
import { formatPrice, formatDate, STATUS_COLORS, ORDER_STATUSES } from '../../utils/helpers';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { getOrders().then(setOrders).finally(() => setLoading(false)); }, []);

  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      setOrders(o => o.map(x => x.id === orderId ? { ...x, status } : x));
      toast.success('Status updated!');
    } catch { toast.error('Failed'); }
  };

  const filtered = orders.filter(o => {
    const matchSearch = !search || o.customerName?.toLowerCase().includes(search.toLowerCase()) || o.orderId?.toLowerCase().includes(search.toLowerCase()) || o.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl font-black text-gray-900">Orders</h1><p className="text-gray-500 text-sm">{orders.length} total orders</p></div>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-56" placeholder="Search orders..."/>
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-36">
          <option value="all">All Status</option>
          {ORDER_STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Spinner/></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Order','Customer','Items','Total','Status','Date','Action'].map(h=>(
                    <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((order, i) => (
                  <motion.tr key={order.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:i*0.03 }}
                    className="hover:bg-gray-50/80 transition-colors">
                    <td className="py-3.5 px-4 font-mono text-xs font-bold text-[#1a3a6e]">#{order.orderId||order.id?.slice(0,8).toUpperCase()}</td>
                    <td className="py-3.5 px-4">
                      <p className="font-semibold text-gray-900">{order.customerName||'Guest'}</p>
                      <p className="text-gray-400 text-xs">{order.phone}</p>
                    </td>
                    <td className="py-3.5 px-4 text-gray-500">{order.items?.length||0}</td>
                    <td className="py-3.5 px-4 font-bold text-gray-900">{formatPrice(order.total||0)}</td>
                    <td className="py-3.5 px-4">
                      <select value={order.status} onChange={e => handleStatusUpdate(order.id, e.target.value)}
                        className={`text-xs font-semibold px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${STATUS_COLORS[order.status]||'bg-gray-100 text-gray-600'}`}>
                        {ORDER_STATUSES.map(s=><option key={s} value={s} className="bg-white text-gray-900 capitalize">{s}</option>)}
                      </select>
                    </td>
                    <td className="py-3.5 px-4 text-gray-400 text-xs">{formatDate(order.createdAt)}</td>
                    <td className="py-3.5 px-4">
                      <Link to={`/admin/orders/${order.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#1a3a6e] hover:text-[#e63946] transition-colors">
                        View
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                      </Link>
                    </td>
                  </motion.tr>
                ))}
                {filtered.length===0&&(
                  <tr><td colSpan={7} className="text-center py-16 text-gray-400">
                    <svg className="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                    No orders found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
export default AdminOrders;
